import type {
  AISessionAssetConfirmations,
  AISessionAssetGroupKey,
  AISessionMessage,
} from '@/types/ai-session';

import { getGenderLabel } from '@/constants/preview-enums';
import type { CanonicalAISession } from '@/lib/ai-session/contracts/types';
import { getSessionAssetResult } from '@/lib/ai-session/contracts/utils';
import { AISessionMessageType } from '@/lib/ai-session/message-type';
import { assetVisibleName, getStoryboardItems } from '@/lib/ai-session/screenplay-workspace/utils';
import { selectStoryboardWorkspaceSnapshot } from '@/lib/ai-session/screenplay-workspace/workspace-session-selectors';
import { resolveStoryboardWorkspaceState } from '@/lib/ai-session/screenplay-workspace/storyboard-review-state';

const ASSET_VALUE_LABELS: Record<string, string> = {
  character: '角色',
  location: '场景',
  prop: '道具',
  main: '主角',
  supporting: '配角',
  extra: '群演',
  male: '男',
  female: '女',
  other: '其他',
  unknown: '未知',
  interior: '内景',
  exterior: '外景',
  indoor: '内景',
  outdoor: '外景',
  interactive: '互动道具',
  set_dressing: '布景道具',
  setDressing: '布景道具',
  character_outfit: '装扮',
  location_set: '布景',
  prop_state: '状态',
};

export type AssetMessageRow = {
  /** 草稿里的名称，编辑请求仍用它定位；展示用 displayName */
  name: string;
  displayName: string;
  draftItemId?: string;
  /** 道具归属，只作展示，不写进名称 */
  ownerLabel?: string;
  typeLabel: string;
  genderLabel: string;
  locationLabel: string;
  tagLabels: string[];
  /** 对照用：有逐字引文时高亮引文，没有引文时滚到所属场次 */
  sourceQuotes?: string[];
  sourceScene?: { sceneId?: string; sceneNumber?: string };
  /** 原始枚举，供 B1 行内编辑 */
  roleType?: string;
  propType?: string;
  gender?: string;
  locationType?: string;
};

export type AlignmentPointStatus = 'aligned' | 'needs_confirmation' | 'missing';

export type AlignmentPointRow = {
  key: string;
  label: string;
  value: string;
  status: AlignmentPointStatus;
  statusLabel: string;
  question: string;
};

const ALIGNMENT_POINT_DEFINITIONS: Array<{ key: string; label: string }> = [
  { key: 'oneLineSynopsis', label: '一句话梗概' },
  { key: 'coreExpression', label: '核心表达' },
  { key: 'sceneStructure', label: '场次结构' },
  { key: 'storyboardRiskWarnings', label: '拆镜风险' },
  { key: 'estimatedDuration', label: '成片长度' },
  { key: 'estimatedStoryboardCount', label: '预估分镜数量' },
];

const ALIGNMENT_STATUS_LABELS: Record<AlignmentPointStatus, string> = {
  aligned: '已对齐',
  needs_confirmation: '待确认',
  missing: '缺失',
};

export const ASSET_GROUP_CONFIRMATION_MESSAGE_BLOCK = 'asset_group_confirmation';

const ALIGNMENT_POINT_LABEL_BY_KEY: Record<string, string> = ALIGNMENT_POINT_DEFINITIONS.reduce(
  (acc, { key, label }) => {
    acc[key] = label;
    return acc;
  },
  {} as Record<string, string>
);

// 内部字段 key 不得直接展示给用户：优先返回中文展示名，命中不到才返回空串由调用方决定兜底。
export function getAlignmentPointLabel(key: string): string {
  return ALIGNMENT_POINT_LABEL_BY_KEY[key] ?? '';
}

export function buildFullAssetConfirmations(): AISessionAssetConfirmations {
  return {
    characters: { names: true, labels: true },
    locations: { names: true, labels: true },
    props: { names: true, labels: true },
  };
}

export function getAssetConfirmationResetKey(session: CanonicalAISession | null): string | null {
  if (session?.sessionType !== 3 || !session.sessionId) return null;
  const version = session.payload?.assetVersion;
  return `${session.sessionId}:${String(version ?? 0)}`;
}

export function hasPersistedSessionAssetIds(session: CanonicalAISession | null): boolean {
  if (session?.sessionType !== 3) return false;
  const persistedAssetIds = session.payload?.persistedAssetIds;
  if (!persistedAssetIds || typeof persistedAssetIds !== 'object') return false;
  return Object.values(persistedAssetIds as Record<string, unknown>).some((value) =>
    Array.isArray(value) ? value.length > 0 : Boolean(value)
  );
}

export function isAssetBreakdownLocked(session: CanonicalAISession | null): boolean {
  if (session?.sessionType !== 3) return false;
  const stageStatus = session.stageStatus;
  return stageStatus === 'confirmed' && hasPersistedSessionAssetIds(session);
}

export const STORYBOARD_FORMAL_WRITE_ASSET_REQUIRED =
  '请先确认资产并写入资产库，再确认分镜写入项目';
export const STORYBOARD_FORMAL_WRITE_ASSET_CHECKING = '正在核对资产是否已写入资产库';
export const STORYBOARD_FORMAL_WRITE_ASSET_UNAVAILABLE = '资产状态暂时无法核对，请稍后重试';

/** 分镜草稿可确认时，正式写入还要求资产已确认入库。未就绪时给出阻断原因。 */
export function resolveStoryboardFormalWriteGate(input: {
  storyboardReady: boolean;
  sourceAssetSessionId?: string | null;
  assetSession?: CanonicalAISession | null;
  assetLookup: 'idle' | 'loading' | 'ready' | 'error';
}): { canWrite: boolean; blockReason: string | null } {
  if (!input.storyboardReady) return { canWrite: false, blockReason: null };
  if (input.assetLookup === 'error') {
    return { canWrite: false, blockReason: STORYBOARD_FORMAL_WRITE_ASSET_UNAVAILABLE };
  }
  if (!input.sourceAssetSessionId) {
    return { canWrite: false, blockReason: STORYBOARD_FORMAL_WRITE_ASSET_REQUIRED };
  }
  if (input.assetLookup !== 'ready') {
    return { canWrite: false, blockReason: STORYBOARD_FORMAL_WRITE_ASSET_CHECKING };
  }
  if (!isAssetBreakdownLocked(input.assetSession ?? null)) {
    return { canWrite: false, blockReason: STORYBOARD_FORMAL_WRITE_ASSET_REQUIRED };
  }
  return { canWrite: true, blockReason: null };
}

const ASSET_DETAIL_IN_FLIGHT_STATUS_RE =
  /完整资产|资产详情补全|批量补全资产详情|提取完整资产|正在生成完整资产/;

/** 观测 B2 详情是否仍在飞：链式标志、activeStage、流式文案。 */
export function isAssetDetailExtractionInFlight(options: {
  isChainingAssetFullDraft?: boolean;
  isStreaming?: boolean;
  streamStatusText?: string | null;
  activeStage?: {
    type?: string | null;
    stageStatus?: string | null;
    assetRunMode?: string | null;
  } | null;
}): boolean {
  if (options.isChainingAssetFullDraft) return true;
  const stage = options.activeStage;
  if (stage?.type === 'asset_breakdown' && stage.assetRunMode === 'detail') {
    if (stage.stageStatus === 'running') return true;
    // 受管执行下 session/activeStage 可能短暂停在 checklist_frozen，但文案已是详情进度。
    if (
      stage.stageStatus === 'checklist_frozen' &&
      ASSET_DETAIL_IN_FLIGHT_STATUS_RE.test(String(options.streamStatusText ?? ''))
    ) {
      return true;
    }
  }
  const statusText = String(options.streamStatusText ?? '').trim();
  if (options.isStreaming && ASSET_DETAIL_IN_FLIGHT_STATUS_RE.test(statusText)) {
    return true;
  }
  return false;
}

export function isAssetBreakdownDetailOnly(session: CanonicalAISession | null): boolean {
  if (session?.sessionType !== 3) return false;
  const stageStatus = session.stageStatus;
  return stageStatus === 'draft_ready';
}

/**
 * B1 清单生成中 / B2 详情生成中：允许「先进入分镜，资产稍后确认」，避免干等。
 * 与工作区 GeneratingShell 对齐，不依赖 session.stageStatus 必须为 running
 *（B2 常停在 checklist_frozen，真正 running 在 activeStage）。
 */
export function shouldShowAssetEarlyStoryboardBlock(options: {
  session: CanonicalAISession | null;
  isChainingAssetFullDraft?: boolean;
  isStreaming?: boolean;
  streamStatusText?: string | null;
  activeStage?: {
    type?: string | null;
    stageStatus?: string | null;
    assetRunMode?: string | null;
  } | null;
}): boolean {
  const { session } = options;
  if (session?.sessionType !== 3) return false;
  if (hasPersistedSessionAssetIds(session)) return false;
  if (isAssetBreakdownLocked(session)) return false;
  const checklist = session.payload?.assetChecklistDraft;
  const hasUsableAssetSkeleton = Boolean(
    checklist &&
    [checklist.characters, checklist.locations, checklist.props].some(
      (items) => Array.isArray(items) && items.length > 0
    )
  );
  // C 阶段至少需要一份可消费的 B1 骨架。首次 B1 尚无结果时展示入口只会被后端拒绝。
  if (!hasUsableAssetSkeleton) return false;
  const stageStatus = session.stageStatus ?? null;
  // 已可入库 / 已确认：改走正式 CTA，不再展示逃生口。
  if (stageStatus === 'draft_ready' || stageStatus === 'confirmed' || stageStatus === 'suspended') {
    return false;
  }

  if (options.isChainingAssetFullDraft) return true;

  if (
    isAssetDetailExtractionInFlight({
      isChainingAssetFullDraft: options.isChainingAssetFullDraft,
      isStreaming: options.isStreaming,
      streamStatusText: options.streamStatusText,
      activeStage: options.activeStage,
    })
  ) {
    return true;
  }

  const active = options.activeStage;
  if (active?.type === 'asset_breakdown') {
    if (active.stageStatus === 'running') return true;
    // B2：领域会话常停 checklist_frozen，activeStage 已切 detail。
    if (active.assetRunMode === 'detail' && stageStatus === 'checklist_frozen') return true;
  }

  if (stageStatus === 'running') return true;
  // 对话提案、澄清和确认保存也会建立流；不能据此推断正在生成资产。

  return false;
}

export function shouldShowAssetPersistedFollowUpBlock(options: {
  session: CanonicalAISession | null;
  /** 分镜会话已存在时仍展示：当前若停在资产页，聊天框上方需要入口 */
  storyboardSessionId?: string | null;
}): boolean {
  return isAssetBreakdownLocked(options.session);
}

/** B `draft_ready` 主 CTA：核对后写入并进分镜。 */
export function shouldShowAssetConfirmPersistBlock(session: CanonicalAISession | null): boolean {
  if (session?.sessionType !== 3) return false;
  if (session.stageStatus !== 'draft_ready') return false;
  if (hasPersistedSessionAssetIds(session)) return false;
  return getSessionAssetResult(session) != null;
}

/** C 阶段详情草稿可确认写入时，在助手展示阶段门控 CTA。 */
export function shouldShowStoryboardConfirmWriteBlock(session: CanonicalAISession | null): boolean {
  if (session?.sessionType !== 4) return false;
  const snapshot = selectStoryboardWorkspaceSnapshot(session);
  if (!snapshot) return false;
  const storyboardCount = getStoryboardItems(session).length;
  return resolveStoryboardWorkspaceState({
    stageStatus: session.stageStatus,
    review: snapshot.review,
    structure: snapshot.structure,
    storyboardCount,
    hasUnassociatedScene: snapshot.hasUnassociatedScene,
  }).canConfirmStoryboard;
}

export function hasAssetGroupConfirmationMessageAnchor(messages: AISessionMessage[]): boolean {
  return messages.some((message) => {
    return isAssetGroupConfirmationMessageAnchor(message);
  });
}

function isAssetGroupConfirmationMessageAnchor(message: AISessionMessage): boolean {
  if (message.senderType !== 'ai') return false;
  const outputPayload = message.outputPayload;
  if (outputPayload?.messageBlock === ASSET_GROUP_CONFIRMATION_MESSAGE_BLOCK) return true;
  return (
    outputPayload?.stage === 'asset_breakdown' &&
    outputPayload?.stageStatus === 'waiting_user' &&
    Boolean(outputPayload?.assetCounts)
  );
}

export function shouldShowAssetGroupConfirmationBlock(
  session: CanonicalAISession | null,
  _messages: AISessionMessage[] = []
): boolean {
  // B1 确认表已迁至工作区；聊天侧不再展示大表（保留函数供工作区判定复用）。
  void _messages;
  if (session?.sessionType !== 3) return false;
  const assetChecklistDraft = session.payload?.assetChecklistDraft;
  if (!assetChecklistDraft || typeof assetChecklistDraft !== 'object') return false;
  if (session.stageStatus !== 'waiting_user') return false;
  return !hasPersistedSessionAssetIds(session);
}

export function shouldShowAssetGroupConfirmationInWorkspace(
  session: CanonicalAISession | null
): boolean {
  return shouldShowAssetGroupConfirmationBlock(session);
}

function isAssetChecklistFrozenMessage(message: AISessionMessage): boolean {
  if (message.senderType !== 'ai') return false;
  const outputPayload = message.outputPayload;
  if (!outputPayload || outputPayload.stage !== 'asset_breakdown') return false;
  if (isAssetGroupConfirmationMessageAnchor(message)) return false;
  return (
    outputPayload.needsFullAssetDraft === true || outputPayload.stageStatus === 'checklist_frozen'
  );
}

/**
 * 资产拆解进度卡可见时收起 STAGE_INTRO。进度结束后恢复。
 * 只改时间线展示，不删除会话消息。分镜引导不走这里。
 */
export function hideAssetStageIntroWhileProgressVisible<T extends { messageType?: number | null }>(
  messages: T[],
  options: {
    isAssetBreakdownSession: boolean;
    showDetailedAgentProgress: boolean;
    progressPhase?: string | null;
  }
): T[] {
  const hide =
    options.isAssetBreakdownSession &&
    options.showDetailedAgentProgress &&
    options.progressPhase === 'asset_breakdown';
  if (!hide) return messages;
  return messages.filter((message) => message.messageType !== AISessionMessageType.STAGE_INTRO);
}

/**
 * 资产拆解时间线消息可见性（方案 A）：
 * - B1（分组确认锚点）：仅 waiting_user 展示；确认后隐藏，避免与冻结/详情态文案冲突
 * - B2（清单已冻结）：仅 checklist_frozen 且非自动链式/详情进行中展示
 */
export function shouldIncludeAssetBreakdownTimelineMessage(
  message: AISessionMessage,
  options: {
    stageStatus: string | null | undefined;
    isChainingAssetFullDraft: boolean;
    isDetailExtractionInFlight?: boolean;
  }
): boolean {
  const stageStatus = String(options.stageStatus ?? '');
  const hideFrozenPrompt =
    options.isChainingAssetFullDraft || Boolean(options.isDetailExtractionInFlight);
  if (isAssetGroupConfirmationMessageAnchor(message)) {
    return stageStatus === 'waiting_user';
  }
  if (isAssetChecklistFrozenMessage(message)) {
    return stageStatus === 'checklist_frozen' && !hideFrozenPrompt;
  }
  return true;
}

export function buildAlignmentPointRows(readingStructured: unknown): AlignmentPointRow[] {
  const source = getObject(readingStructured);
  const checklist = Array.isArray(source?.alignmentChecklist) ? source.alignmentChecklist : [];
  const itemByKey = new Map<string, Record<string, unknown>>();
  checklist.forEach((item) => {
    const record = getObject(item);
    const key = getStringField(record, ['key']);
    if (key) itemByKey.set(key, record ?? {});
  });

  return ALIGNMENT_POINT_DEFINITIONS.map(({ key, label }) => {
    const item = itemByKey.get(key);
    const status = normalizeAlignmentStatus(getStringField(item, ['status']));
    return {
      key,
      label: getStringField(item, ['label']) || label,
      value: getStringField(item, ['value', 'summaryText']) || '待补充',
      status,
      statusLabel: ALIGNMENT_STATUS_LABELS[status],
      question: getStringField(item, ['question']),
    };
  });
}

export function hasMeaningfulAlignmentChecklist(readingStructured: unknown): boolean {
  const source = getObject(readingStructured);
  const checklist = Array.isArray(source?.alignmentChecklist) ? source.alignmentChecklist : [];
  return checklist.some((item) => {
    const record = getObject(item);
    if (!record) return false;
    const value = getStringField(record, ['value', 'summaryText']);
    const status = normalizeAlignmentStatus(getStringField(record, ['status']));
    return Boolean(value || status !== 'missing');
  });
}

export function shouldShowAlignmentPointBlock({
  isAlignmentSession,
  isReadthroughComplete,
  readingStructured,
}: {
  isAlignmentSession: boolean;
  isReadthroughComplete: boolean;
  readingStructured: unknown;
}): boolean {
  return Boolean(
    isAlignmentSession &&
    isReadthroughComplete &&
    hasMeaningfulAlignmentChecklist(readingStructured)
  );
}

export function getObject(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

export function getStringField(
  record: Record<string, unknown> | null | undefined,
  keys: string[]
): string {
  if (!record) return '';
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number') return String(value);
  }
  return '';
}

function normalizeAlignmentStatus(value: string): AlignmentPointStatus {
  if (value === 'aligned' || value === 'needs_confirmation' || value === 'missing') {
    return value;
  }
  return 'missing';
}

function toDisplayLabel(value: string): string {
  const normalized = value.trim();
  if (!normalized) return '';
  const key = normalized.toLowerCase();
  return ASSET_VALUE_LABELS[key] ?? normalized;
}

function normalizeChecklistEnumCode(
  value: string,
  allowedCodes: readonly string[],
  aliases: Record<string, string>
): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const lower = trimmed.toLowerCase();
  if (allowedCodes.includes(lower)) return lower;
  const fromLabel = Object.entries(ASSET_VALUE_LABELS).find(([, label]) => label === trimmed)?.[0];
  if (fromLabel && allowedCodes.includes(fromLabel)) return fromLabel;
  for (const [pattern, code] of Object.entries(aliases)) {
    if (trimmed.includes(pattern) || lower.includes(pattern.toLowerCase())) return code;
  }
  return undefined;
}

function normalizeChecklistRoleType(value: string): string | undefined {
  return normalizeChecklistEnumCode(value, ['main', 'supporting', 'extra'], {
    主角: 'main',
    主演: 'main',
    配角: 'supporting',
    次要: 'supporting',
    群演: 'extra',
    群众: 'extra',
    路人: 'extra',
    background: 'extra',
  });
}

function normalizeChecklistGender(value: string): string | undefined {
  return normalizeChecklistEnumCode(value, ['male', 'female', 'other', 'unknown'], {
    男: 'male',
    女: 'female',
    其他: 'other',
    未知: 'unknown',
  });
}

function normalizeChecklistLocationType(value: string): string | undefined {
  return normalizeChecklistEnumCode(value, ['interior', 'exterior'], {
    内景: 'interior',
    室内: 'interior',
    indoor: 'interior',
    外景: 'exterior',
    室外: 'exterior',
    outdoor: 'exterior',
  });
}

function normalizeChecklistPropType(value: string): string | undefined {
  return normalizeChecklistEnumCode(value, ['interactive', 'set_dressing'], {
    互动: 'interactive',
    布景: 'set_dressing',
    陈设: 'set_dressing',
    setdressing: 'set_dressing',
    setDressing: 'set_dressing',
  });
}

function getItemMetaData(record: Record<string, unknown> | null): Record<string, unknown> | null {
  return getObject(record?.metaData);
}

function formatCharacterGenderLabel(record: Record<string, unknown> | null): string {
  const metaData = getItemMetaData(record);
  const genderRaw = getStringField(record, ['gender']) || getStringField(metaData, ['gender']);
  if (!genderRaw) return '待补充';
  const genderCode = normalizeChecklistGender(genderRaw) ?? genderRaw;
  return getGenderLabel(genderCode) || toDisplayLabel(genderCode) || genderRaw;
}

export function readAssetScriptSource(value: unknown): {
  quotes: string[];
  scene?: { sceneId?: string; sceneNumber?: string };
} {
  const record = getObject(value);
  const variants = Array.isArray(record?.variants) ? record.variants : [];
  const quotes: string[] = [];
  let sceneNumber: string | undefined;
  let sceneId: string | undefined;
  for (const variant of variants) {
    const variantRecord = getObject(variant);
    if (!sceneNumber && Array.isArray(variantRecord?.applicableScenes)) {
      const first = variantRecord.applicableScenes.find(
        (item) => typeof item === 'string' && item.trim()
      );
      if (typeof first === 'string') sceneNumber = first;
    }
    const evidence = getObject(variantRecord?.metaData)?.sourceEvidence;
    if (!Array.isArray(evidence)) continue;
    for (const item of evidence) {
      const evidenceRecord = getObject(item);
      const quote = getStringField(evidenceRecord, ['quote']);
      if (quote) quotes.push(quote);
      sceneId = sceneId || getStringField(evidenceRecord, ['sceneId']) || undefined;
      sceneNumber = sceneNumber || getStringField(evidenceRecord, ['sceneNumber']) || undefined;
    }
  }
  return {
    quotes,
    scene: sceneNumber || sceneId ? { sceneNumber, sceneId } : undefined,
  };
}

function extractAssetTagLabels(record: Record<string, unknown> | null): string[] {
  if (!record) return [];
  const variants = Array.isArray(record.variants) ? record.variants : [];
  const labels = variants
    .map((variant) => getStringField(getObject(variant), ['tagLabel', 'label', 'name']))
    .filter(Boolean);
  if (labels.length > 0) return labels;

  const metaData = getItemMetaData(record);
  const fallback = getStringField(metaData, [
    'defaultTagLabel',
    'tagLabel',
    'outfitName',
    'setName',
    'stateName',
  ]);
  return fallback ? [fallback] : [];
}

export function buildAssetMessageRows(
  assetResult: unknown,
  group: AISessionAssetGroupKey
): AssetMessageRow[] {
  const source = getObject(assetResult);
  const items = source?.[group];
  if (!Array.isArray(items)) return [];

  return items.reduce<AssetMessageRow[]>((rows, item) => {
    const record = getObject(item);
    const name = getStringField(record, ['name']);
    if (!name) return rows;
    const tagLabels = extractAssetTagLabels(record);
    const source = readAssetScriptSource(record);
    const metadata = getItemMetaData(record);
    const ownerLabel = [
      getStringField(record, ['ownerCharacter']),
      getStringField(record, ['ownerLocation']),
    ]
      .filter(Boolean)
      .join(' / ');
    const draftItemId = getStringField(record, ['draftItemId']);
    const typeCode = getStringField(record, [
      group === 'props' ? 'propType' : 'roleType',
      'kind',
      'type',
    ]);
    const normalizedTypeCode =
      group === 'characters'
        ? normalizeChecklistRoleType(typeCode)
        : group === 'props'
          ? normalizeChecklistPropType(typeCode)
          : typeCode
            ? typeCode
            : undefined;
    const typeLabel =
      (normalizedTypeCode ? toDisplayLabel(normalizedTypeCode) : '') ||
      toDisplayLabel(typeCode) ||
      '待补充';
    const genderCodeRaw =
      group === 'characters' ? getStringField(record, ['gender', 'sex']) : undefined;
    const genderCode =
      group === 'characters' && genderCodeRaw ? normalizeChecklistGender(genderCodeRaw) : undefined;
    const genderLabel = group === 'characters' ? formatCharacterGenderLabel(record) : '';
    const locationCodeRaw = getStringField(record, ['location', 'locationType', 'sceneType']);
    const locationCode =
      group === 'locations' && locationCodeRaw
        ? normalizeChecklistLocationType(locationCodeRaw)
        : locationCodeRaw || undefined;
    const locationLabel =
      (locationCode ? toDisplayLabel(locationCode) : '') ||
      toDisplayLabel(locationCodeRaw) ||
      (group === 'locations' ? '待补充' : '');
    rows.push({
      name,
      displayName: assetVisibleName({ name, metaData: metadata }),
      ...(draftItemId ? { draftItemId } : {}),
      ...(ownerLabel ? { ownerLabel } : {}),
      typeLabel,
      genderLabel,
      locationLabel,
      tagLabels,
      sourceQuotes: source.quotes,
      sourceScene: source.scene,
      ...(group === 'characters'
        ? {
            roleType: normalizedTypeCode || undefined,
            gender: genderCode || undefined,
          }
        : {}),
      ...(group === 'props' ? { propType: normalizedTypeCode || undefined } : {}),
      ...(group === 'locations' ? { locationType: locationCode || undefined } : {}),
    });
    return rows;
  }, []);
}

export function getAssetConfirmationDisplayRows(rows: AssetMessageRow[]): AssetMessageRow[] {
  return rows;
}
